import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import GeneralForm from '../../components/generalForm';
import { useRoutePlan, useRoutePlanCreate, useRoutePlanEdit } from '../apiHooks';
import utils from '../../utils/utils';
import DATE_FORMAT from '../../components/constants/dateFormat';
import DateField from '../../components/customFields/dateField';
import RoutePlanVehicles from '../../components/customFields/routePlanVehicles';
import GooglePlacesField from '../../components/customFields/googlePlacesField';
import RoutePlanPickupSlots from '../../components/customFields/routePlanPickupSlots';

function RoutePlanForm({ intl, dateParam, routePlanId, onSuccess }) {
  const errorMessagesRef = useRef();

  const deliveryDate = dateParam ? utils.getDateFromString(dateParam, DATE_FORMAT.url) : null;

  const [formRoutePlan, setFormRoutePlan] = useState({
    name:
      intl.formatMessage(
        { id: 'route.plan.form.field.name.default.value' },
        { date: deliveryDate?.format(DATE_FORMAT.displayWeekdayAndDate) }
      ) || '',
    deliveryDate: deliveryDate?.format(DATE_FORMAT.dataTransfer),
    vehicles: [],
  });

  const {
    isLoading: loading,
    isError,
    error,
  } = useRoutePlan({
    routePlanId: routePlanId,
    onSuccess: (routePlan) => {
      const deliveryDate = utils.getDateFromString(routePlan.deliveryDate);
      deliveryDate.add(12, 'hours'); // TOOD remove UTC hack and treat all dates as UTC on back-end - set AWS timezone to UTC

      setFormRoutePlan({
        ...routePlan,
        deliveryDate: deliveryDate.format(DATE_FORMAT.dataTransfer),
        vehicles: routePlan.vehicles.map((vehicle) => ({
          ...vehicle,
          count: `${vehicle.count}`,
          capacity: `${vehicle.capacity}`,
        })),
        pickupSlots: routePlan.pickupSlots.map((pickupSlot) => {
          const pickupFrom = utils.getDateFromString(pickupSlot.pickupFrom);
          const pickupTo = utils.getDateFromString(pickupSlot.pickupTo);

          return {
            pickupFromHour: pickupFrom.format('HH'),
            pickupFromMinutes: pickupFrom.format('mm'),
            pickupToHour: pickupTo.format('HH'),
            pickupToMinutes: pickupTo.format('mm'),
          };
        }),
      });
    },
  });

  const {
    editRoutePlan,
    isLoading: editLoading,
    isError: isEditError,
    error: editError,
  } = useRoutePlanEdit(() => {
    onSuccess();
  });

  const {
    createRoutePlan,
    isLoading: createLoading,
    isError: isCreateError,
    error: createError,
  } = useRoutePlanCreate(() => onSuccess());

  useEffect(() => {
    if (editError || createError) {
      errorMessagesRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [editError, createError]);

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      name: {
        type: 'string',
        title: intl.formatMessage({ id: 'route.plan.form.field.name.label' }),
      },
      deliveryDate: {
        type: 'string',
        title: intl.formatMessage({ id: 'route.plan.form.field.delivery.date.label' }),
      },
      vehicles: {
        type: 'array',
      },
      pickupSlots: {
        type: 'array',
      },
      startingAddress: {
        title: intl.formatMessage({ id: 'route.plan.form.field.starting.address.label' }),
        type: 'object',
        required: [],
        properties: {
          unit: {
            type: 'string',
            default: '',
          },
          street: {
            type: 'string',
            default: '',
          },
          city: {
            type: 'string',
            default: '',
          },
          country: {
            type: 'string',
            default: '',
          },
          postCode: {
            type: 'string',
            default: '',
          },
          coordinates: {
            type: 'object',
            required: [],
            properties: {
              lat: {
                type: 'string',
              },
              lon: {
                type: 'string',
              },
            },
          },
        },
      },
    },
  };

  const uiSchema = {
    deliveryDate: {
      'ui:field': 'dateField',
    },
    vehicles: {
      'ui:field': 'routePlanVehicles',
    },
    pickupSlots: {
      'ui:field': 'routePlanPickupSlots',
    },
    startingAddress: {
      'ui:field': 'googlePlacesField',
    },
  };

  const onFormSubmit = () => {
    if (routePlanId) {
      editRoutePlan({ routePlan: formRoutePlan });
    } else {
      createRoutePlan({ routePlan: formRoutePlan });
    }
  };

  const formFields = {
    dateField: DateField,
    routePlanVehicles: RoutePlanVehicles,
    routePlanPickupSlots: RoutePlanPickupSlots,
    googlePlacesField: GooglePlacesField,
  };

  const buttonCopy = routePlanId ? 'route.plan.form.cta.edit' : 'route.plan.form.cta.create';

  return (
    <div className="m-b-6">
      <GeneralForm
        title={
          routePlanId
            ? intl.formatMessage({ id: 'route.plan.form.title.edit' }, { routePlanName: formRoutePlan.name })
            : intl.formatMessage({ id: 'route.plan.form.title.create' })
        }
        onChange={(event) => setFormRoutePlan(event.formData)}
        onSubmit={onFormSubmit}
        formSchema={formSchema}
        formUiSchema={uiSchema}
        formFields={formFields}
        formData={formRoutePlan}
        buttonCopy={buttonCopy}
        errorMessageRef={errorMessagesRef}
        errorMessage={
          isError || isEditError || isCreateError
            ? utils.getErrorMessageFromResponse(error || editError || createError, true)
            : null
        }
        loading={loading || editLoading || createLoading}
        hideImageUpload={true}
      />
    </div>
  );
}

RoutePlanForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

const mapDispatchToProps = {};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(RoutePlanForm));
